// Local storage utilities for caching data

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiresIn: number; // milliseconds
}

class LocalStorageCache {
  private prefix = 'amchi-mumbai-';

  // Set item in cache with expiration
  set<T>(key: string, data: T, expiresInMs: number = 24 * 60 * 60 * 1000): void {
    try {
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        expiresIn: expiresInMs
      };
      
      localStorage.setItem(this.prefix + key, JSON.stringify(item));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      // Handle quota exceeded or other localStorage errors
      this.cleanup();
    }
  }

  // Get item from cache
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(this.prefix + key);
      
      if (!item) {
        return null;
      }

      const cached: CacheItem<T> = JSON.parse(item);
      
      // Check if expired
      if (Date.now() - cached.timestamp > cached.expiresIn) {
        this.remove(key);
        return null;
      }

      return cached.data;
    } catch (error) {
      console.error('Failed to get from localStorage:', error);
      return null;
    }
  }

  // Remove item from cache
  remove(key: string): void {
    try {
      localStorage.removeItem(this.prefix + key);
    } catch (error) {
      console.error('Failed to remove from localStorage:', error);
    }
  }

  // Check if item exists and is not expired
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  // Clear all cached items
  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Failed to clear localStorage cache:', error);
    }
  }

  // Clean up expired items
  cleanup(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            try {
              const cached: CacheItem<any> = JSON.parse(item);
              if (Date.now() - cached.timestamp > cached.expiresIn) {
                localStorage.removeItem(key);
              }
            } catch {
              // Invalid JSON, remove it
              localStorage.removeItem(key);
            }
          }
        }
      });
    } catch (error) {
      console.error('Failed to cleanup localStorage:', error);
    }
  }

  // Get cache size in bytes (approximate)
  getSize(): number {
    let size = 0;
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          const item = localStorage.getItem(key);
          if (item) {
            size += key.length + item.length;
          }
        }
      });
    } catch (error) {
      console.error('Failed to calculate cache size:', error);
    }
    return size;
  }

  // Get all cached keys
  getKeys(): string[] {
    try {
      const keys = Object.keys(localStorage);
      return keys
        .filter(key => key.startsWith(this.prefix))
        .map(key => key.replace(this.prefix, ''));
    } catch (error) {
      console.error('Failed to get cache keys:', error);
      return [];
    }
  }
}

// Create singleton instance
export const cache = new LocalStorageCache();

// Specific cache functions for translation data
export const translationCache = {
  set: (phrase: string, translation: any) => {
    cache.set(`translation-${phrase.toLowerCase()}`, translation, 7 * 24 * 60 * 60 * 1000); // 7 days
  },
  
  get: (phrase: string) => {
    return cache.get(`translation-${phrase.toLowerCase()}`);
  },
  
  has: (phrase: string) => {
    return cache.has(`translation-${phrase.toLowerCase()}`);
  }
};

// User preferences cache
export const preferencesCache = {
  set: (preferences: any) => {
    cache.set('user-preferences', preferences, 30 * 24 * 60 * 60 * 1000); // 30 days
  },
  
  get: () => {
    return cache.get('user-preferences');
  }
};

// Initialize cleanup on load
if (typeof window !== 'undefined') {
  // Clean up expired items on app start
  cache.cleanup();
  
  // Set up periodic cleanup (every hour)
  setInterval(() => {
    cache.cleanup();
  }, 60 * 60 * 1000);
}